import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import JourneyStepper from '../components/JourneyStepper';
import { FoilOptionsPicker } from '../components/FoilOptionsPicker';
import { ProductionSizeModal } from './UploadArtwork/ProductionSizeModal';
import { PageMakerBanner } from './UploadArtwork/PageMakerBanner';
import { PageMakerEditor } from './UploadArtwork/PageMakerEditor';
import LivePreviewStage from './UploadArtwork/LivePreviewStage';
import InlineProductionSizeAlert from './UploadArtwork/InlineProductionSizeAlert';
import { CutlineShapePicker } from './UploadArtwork/CutlineShapePicker';
import { ArtworkDropzone } from './UploadArtwork/ArtworkDropzone';
import { useArtworkFiles } from '../hooks/useArtworkFiles';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ------------------------------------------------------------------------
// Page-Maker tile math constants — used by stickerTilesPerSheet derivation.
// ------------------------------------------------------------------------
const MM_PER_INCH = 25.4;
const CM_PER_INCH = 2.54;
// Standard letter page (inches) minus a 0.25" safety margin on each side.
const LETTER_PAGE_WIDTH_IN = 8.5;
const LETTER_PAGE_HEIGHT_IN = 11;
const PAGE_SAFETY_MARGIN_IN = 0.25;
const USABLE_WIDTH_IN = LETTER_PAGE_WIDTH_IN - 2 * PAGE_SAFETY_MARGIN_IN;
const USABLE_HEIGHT_IN = LETTER_PAGE_HEIGHT_IN - 2 * PAGE_SAFETY_MARGIN_IN;

const UploadArtwork = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [orderId, setOrderId] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [uploading, setUploading] = useState(false);
  const [cutlineShape, setCutlineShape] = useState('');
  const [order, setOrder] = useState(null);
  const [foilType, setFoilType] = useState('gold');
  const [foilColorHex, setFoilColorHex] = useState(null);
  const [productionSizeInfo, setProductionSizeInfo] = useState(null);
  // Freeform Page-Maker layout — `null` means "use the auto-grid".
  const [pageMakerLayout, setPageMakerLayout] = useState(null);
  const [showPageMakerEditor, setShowPageMakerEditor] = useState(false);

  // Artwork file collection — owns drag/drop state, file list, thumbnails.
  const {
    files,
    handleFileSelect,
    removeFile,
    filePreviews,
    isDragActive,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
  } = useArtworkFiles();

  useEffect(() => {
    // Wait for AuthContext to finish hydrating before deciding what to
    // do — otherwise a direct deep-link (or a hard refresh) sees
    // `isAuthenticated=false` for one render frame and bounces us to
    // /login before the cookie-backed /me call resolves.
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Get orderId and shippingAddress from location state
    if (location.state?.orderId) {
      setOrderId(location.state.orderId);
      if (location.state.itemId) setItemId(location.state.itemId);
      // Fetch order to detect foil product and initialize foil options
      (async () => {
        try {
          const { data } = await axios.get(`${API_URL}/api/enhanced-orders/${location.state.orderId}`);
          setOrder(data);
          if (data?.foil_type) setFoilType(data.foil_type);
          if (data?.foil_color_hex) setFoilColorHex(data.foil_color_hex);
        } catch (e) {
          log.error('Failed to fetch order for foil detection:', e);
        }
      })();
    } else {
      navigate('/quote');
    }
  }, [authLoading, isAuthenticated, location, navigate]);

  const probe = `${(order?.items?.[0]?.material || '').toLowerCase()} ${(order?.items?.[0]?.product_type || '').toLowerCase()}`;
  const isFoilProduct = probe.includes('foil') || probe.includes('gold & silver') || probe.includes('gold-silver');
  // Page-Maker: user uploads MULTIPLE different designs, auto-tiled on each sheet.
  // Flag comes from the backend (set from mockData pageMaker flag on subcategory/material).
  const isPageMaker = !!(order?.is_page_maker || order?.items?.[0]?.page_maker);

  // Calculate how many stickers tile per 8.5x11" sheet for the page-maker preview
  const stickerTilesPerSheet = (() => {
    if (!isPageMaker) return null;
    const w = parseFloat(order?.items?.[0]?.width) || 2;
    const h = parseFloat(order?.items?.[0]?.height) || 2;
    const unit = (order?.items?.[0]?.unit || 'inch').toLowerCase();
    // convert to inches
    const toInch = (v) => {
      if (unit.startsWith('cm')) return v / CM_PER_INCH;
      if (unit.startsWith('mm')) return v / MM_PER_INCH;
      return v;
    };
    const wIn = toInch(w);
    const hIn = toInch(h);
    // Reserve ~0.85" at the top for the Page-Maker header strip.
    const HEADER_RESERVE_IN = 0.85;
    const usableW = USABLE_WIDTH_IN;
    const usableH = USABLE_HEIGHT_IN - HEADER_RESERVE_IN;
    const GUTTER_IN = 0.125;
    const cols = Math.max(1, Math.floor((usableW + GUTTER_IN) / (wIn + GUTTER_IN)));
    const rows = Math.max(1, Math.floor((usableH + GUTTER_IN) / (hIn + GUTTER_IN)));
    return { cols, rows, total: cols * rows };
  })();

  const buildFoilQuery = () => {
    if (!isFoilProduct) return '';
    const params = [`foil_type=${encodeURIComponent(foilType)}`];
    if (foilType === 'colored' && foilColorHex) {
      params.push(`foil_color_hex=${encodeURIComponent(foilColorHex)}`);
    }
    return '&' + params.join('&');
  };

  const handleSkip = async () => {
    // Validate cutline shape is selected
    if (!cutlineShape) {
      alert('Please select a cutline shape before continuing.');
      return;
    }
    if (isFoilProduct && foilType === 'colored' && !foilColorHex) {
      alert('Please pick a foil color before continuing.');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/enhanced-orders/${orderId}/shipping-info?cutline_shape=${encodeURIComponent(cutlineShape)}${buildFoilQuery()}`,
        { skip_artwork: true }
      );
      navigate('/order-summary', {
        state: {
          orderId,
          shippingAddress: location.state?.shippingAddress,
          country: location.state?.country
        }
      });
    } catch (error) {
      log.error('Skip-artwork flow failed:', error);
      alert('Could not skip artwork. Please try again.');
    }
  };

  const saveFoilOptions = async () => {
    if (!isFoilProduct) return;
    try {
      const qs = foilType === 'colored' && foilColorHex
        ? `foil_type=colored&foil_color_hex=${encodeURIComponent(foilColorHex)}`
        : `foil_type=${encodeURIComponent(foilType)}`;
      await axios.patch(`${API_URL}/api/enhanced-orders/${orderId}/foil-options?${qs}`);
    } catch (e) {
      // Non-fatal: foil options will be re-saved at Review Order step if needed
      log.warn('Failed to save foil options (will retry at review step):', e);
    }
  };

  const handleContinue = async () => {
    // Validate cutline shape is selected
    if (!cutlineShape) {
      alert('Please select a cutline shape before continuing.');
      return;
    }
    if (isFoilProduct && foilType === 'colored' && !foilColorHex) {
      alert('Please pick a foil color before continuing.');
      return;
    }

    await saveFoilOptions();

    // Allow continuing without files if user wants to skip
    if (files.length === 0) {
      navigate('/order-summary', {
        state: {
          orderId,
          shippingAddress: location.state?.shippingAddress,
          country: location.state?.country
        }
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('cutline_shape', cutlineShape); // Include cutline shape
      if (itemId) formData.append('item_id', itemId); // Multi-item: bind to this item
      // Attach the freeform layout when the customer is using the editor.
      // Backend parses this as a JSON list and pins it onto items.$.page_maker_layout.
      if (isPageMaker && showPageMakerEditor && pageMakerLayout?.length) {
        formData.append(
          'page_maker_layout',
          JSON.stringify(
            pageMakerLayout.map((t) => ({
              design_idx: t.design_idx,
              x_in: t.x_in,
              y_in: t.y_in,
              w_in: t.w_in,
              h_in: t.h_in,
              shape: t.shape,
            })),
          ),
        );
      }
      files.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(
        `${API_URL}/api/files/upload-artwork`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
          // Cookie automatically sent with request
        }
      );

      // For custom cutlines, fetch the production-size estimate and surface
      // it as a passive warning — no longer gates progression. Customer sees
      // the banner during the brief upload→navigate interval plus on the
      // review page (where the info persists server-side on the order).
      if (cutlineShape === 'custom') {
        try {
          const est = await axios.get(
            `${API_URL}/api/enhanced-orders/${orderId}/production-size-estimate`
          );
          if (est.data?.applicable && est.data?.was_adjusted) {
            setProductionSizeInfo(est.data);
          }
        } catch (e) {
          // Non-fatal: skip the heads-up banner, continue to review page
          log.warn('Production-size estimate fetch failed:', e);
        }
      }

      // Proceed to next page - don't save instructions yet
      // Instructions will be saved in Review Order page
      navigate('/order-summary', {
        state: {
          orderId,
          instructions,
          shippingAddress: location.state?.shippingAddress,
          country: location.state?.country
        }
      });
    } catch (error) {
      log.error('Artwork upload failed:', error);
      alert('Failed to upload artwork. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const proceedFromProductionWarning = () => {
    setProductionSizeInfo(null);
    navigate('/order-summary', {
      state: {
        orderId,
        instructions,
        shippingAddress: location.state?.shippingAddress,
        country: location.state?.country,
      },
    });
  };

  const goBackFromProductionWarning = () => {
    setProductionSizeInfo(null);
    // User wants to change the size — drop them on the quote step for this order
    navigate('/quote', { state: { orderId, editSize: true } });
  };

  // Memoized so FoilOptionsPicker doesn't re-render on every keystroke of
  // unrelated form fields (fixes a RR-flagged inline-object prop issue).
  const foilValue = useMemo(
    () => ({ foilType, foilColorHex }),
    [foilType, foilColorHex]
  );
  const handleFoilChange = useCallback(
    ({ foilType: ft, foilColorHex: fh }) => {
      setFoilType(ft);
      setFoilColorHex(fh);
    },
    [] // useState setters are stable; ft/fh are parameters (not closed-over vars)
  );

  // Stable default-size prop for PageMakerEditor — avoids feeding a fresh
  // object reference on every parent render (which would reset the
  // editor's seeded layout every time UploadArtwork state changed).
  const pageMakerDefaultSize = useMemo(
    () => ({
      w: order?.items?.[0]?.width || 2,
      h: order?.items?.[0]?.height || 2,
      unit: order?.items?.[0]?.unit || 'inch',
    }),
    [order]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* iter114s.45 — Per-page brand band removed; the global TopNav
          + the unified JourneyStepper carry all the chrome the
          customer needs. */}
      <JourneyStepper currentStage={2} orderId={orderId} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Upload Artwork</h1>
          <button
            onClick={handleSkip}
            data-testid="upload-skip-btn"
            className="text-[#4A7C59] hover:underline text-sm"
          >
            Or Skip and Email Artwork Later
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT COLUMN — configuration form */}
          <div className="lg:col-span-5 flex flex-col gap-8 bg-white rounded-2xl shadow-lg p-6 lg:p-8">

          {/* Page-Maker banner — shown for multi-design page products */}
          {isPageMaker && <PageMakerBanner tiles={stickerTilesPerSheet} files={files} />}

          {/* Page-Maker editor toggle — power-user freeform layout */}
          {isPageMaker && files.length > 0 && (
            <div className="-mt-2 mb-4">
              <button
                type="button"
                onClick={() => setShowPageMakerEditor((v) => !v)}
                data-testid="toggle-page-maker-editor"
                className="text-xs font-semibold text-[#4A7C59] hover:bg-[#4A7C59]/10 border border-[#4A7C59]/40 rounded-full px-3 py-1.5 transition-colors"
              >
                {showPageMakerEditor
                  ? '← Back to Auto-Tile'
                  : 'Customise layout · Drag & resize each design →'}
              </button>
            </div>
          )}

          {/* Page-Maker freeform editor — drag/resize/mix designs */}
          {isPageMaker && showPageMakerEditor && files.length > 0 && (
            <PageMakerEditor
              files={files}
              filePreviews={filePreviews}
              defaultSize={pageMakerDefaultSize}
              cutlineShape={cutlineShape}
              onChange={setPageMakerLayout}
            />
          )}

          {/* File Upload Section — drag-drop + selected-files list + ADD-MORE button */}
          <ArtworkDropzone
            files={files}
            isPageMaker={isPageMaker}
            isDragActive={isDragActive}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onFileSelect={handleFileSelect}
            onRemoveFile={removeFile}
          />

          {/* Cutline Shape Selector */}
          <CutlineShapePicker
            cutlineShape={cutlineShape}
            setCutlineShape={setCutlineShape}
          />

          {/* Foil Options (only for Gold & Silver Foil Rolls) */}
          {isFoilProduct && (
            <div className="p-4 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg">
              <FoilOptionsPicker
                value={foilValue}
                onChange={handleFoilChange}
              />
              {foilType === 'colored' && !foilColorHex && (
                <p className="mt-3 text-sm text-red-600">
                  * Pick a preset or enter a custom hex color to continue
                </p>
              )}
            </div>
          )}

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Instructions (optional)
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4A7C59] focus:border-transparent"
              placeholder="Enter any special instructions for your artwork..."
            />
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={uploading}
            data-testid="continue-checkout-button"
            className="w-full bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white py-4 text-lg"
          >
            {uploading ? 'Uploading...' : 'CONTINUE'}
          </Button>
          </div>

          {/* RIGHT COLUMN — live preview + inline production-size callout */}
          <div className="lg:col-span-7 lg:sticky lg:top-8 flex flex-col gap-5">
            <LivePreviewStage
              file={files[0] || null}
              cutlineShape={cutlineShape}
              width={order?.items?.[0]?.width}
              height={order?.items?.[0]?.height}
            />
            <InlineProductionSizeAlert
              info={productionSizeInfo}
              onAccept={proceedFromProductionWarning}
              onChangeSize={goBackFromProductionWarning}
            />
          </div>
        </div>
      </div>

      {/* Fallback: the old blocking modal stays mounted but never opens
          by default — kept only so any deep-linked test that relied on it
          doesn't 404. New flow uses the inline alert in the right column. */}
      <ProductionSizeModal
        info={null}
        onGoBack={goBackFromProductionWarning}
        onContinue={proceedFromProductionWarning}
      />
    </div>
  );
};

export default UploadArtwork;
